import type { ExternalUserFormData } from "@/schemas/users/external-user";
import type { ApiExternalUserResponseType } from "@/types/entities/external-users";
import axios from "@/utils/axios";
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { isLegalPersonDocument } from "@/utils/document-types";

export type ExternalUsersFiltersProps = {
  page?: number;
  per_page?: number;
  search?: string;
  name?: string;
  sort_by?: string;
  sort_direction?: "asc" | "desc";
  relationships?: string[];
  group_by?: string;
  status?: string;
  user_type?: string | string[];
  institution_id?: string;
  document_type?: string;
  created_at?: string;
};

export const useGetExternalUsers = (allFilters: ExternalUsersFiltersProps) => {
  const filters = {
    ...allFilters,
    page: allFilters.page || 1,
    per_page: allFilters.per_page || 10,
  };

  return useQuery({
    queryKey: ["external-users", filters],
    queryFn: async (): Promise<ApiExternalUserResponseType> => {
      const params = new URLSearchParams();
      params.append("page", String(filters.page));
      params.append("per_page", String(filters.per_page));
      if (filters.search) params.append("filter[search]", filters.search);
      if (filters.name) params.append("filter[name]", filters.name);
      if (filters.status) params.append("filter[status]", filters.status);
      if (filters.user_type) {
        const arr = Array.isArray(filters.user_type)
          ? filters.user_type
          : [filters.user_type];
        arr.forEach((type) => params.append("filter[type][]", type));
      }
      if (filters.document_type)
        params.append("filter[document_type]", filters.document_type);
      if (filters.created_at)
        params.append("filter[created_at]", filters.created_at);
      if (filters.group_by) params.append("order_by", filters.group_by);
      if (filters.relationships)
        params.append("relationships", filters.relationships.join(","));

      const response = await axios.get(
        `/admin/v1/applicant?${params.toString()}`,
      );
      return response.data;
    },
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
  });
};

export const useGetExternalUsersAsInfinite = (
  allFilters: ExternalUsersFiltersProps,
  enabled = true,
) => {
  const filters = {
    ...allFilters,
    per_page: allFilters.per_page || 10,
    group_by: allFilters.group_by || "created_at",
  };

  return useInfiniteQuery({
    queryKey: ["external-users-infinite", filters],
    queryFn: async ({
      pageParam = 1,
    }): Promise<ApiExternalUserResponseType> => {
      const response = await axios.get("/admin/v1/external-users", {
        params: {
          page: pageParam,
          per_page: filters.per_page,
          order_by: filters.group_by,
          filter: {
            ...(filters.search && { search: filters.search }),
            ...(filters.status && { status: filters.status }),
            ...(filters.user_type && { user_type: filters.user_type }),
            ...(filters.institution_id && {
              institution_id: filters.institution_id,
            }),
          },
          relationships: filters.relationships
            ? filters.relationships.join(",")
            : undefined,
        },
      });
      return response.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const { current_page, last_page } = (
        lastPage as ApiExternalUserResponseType
      ).meta;
      if (current_page < last_page) {
        return current_page + 1;
      }
    },
    enabled,
  });
};

export const useCreateExternalUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: ExternalUserFormData) => {
      const payload: Record<string, unknown> = {
        type: data.type,
        document_type: data.document_type,
        document_number: data.document_number,
        first_name: data.first_name,
        email: data.email,
        phone: data.phone,
      };

      if (isLegalPersonDocument(data.document_type)) {
        payload.last_name = null;
      } else if (data.last_name?.trim()) {
        payload.last_name = data.last_name;
      }

      if (data.commercial_name?.trim()) {
        payload.commercial_name = data.commercial_name;
      }

      if (data.birthdate?.trim()) {
        payload.birthdate = data.birthdate;
      }

      const response = await axios.post("/admin/v1/applicant", payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["external-users"],
      });
    },
  });
};

export const useUpdateExternalUser = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: ExternalUserFormData) => {
      const payload: Record<string, unknown> = {
        type: data.type,
        document_type: data.document_type,
        document_number: data.document_number,
        first_name: data.first_name,
        email: data.email,
        phone: data.phone,
      };

      if (isLegalPersonDocument(data.document_type)) {
        payload.last_name = null;
      } else if (data.last_name?.trim()) {
        payload.last_name = data.last_name;
      }

      if (data?.commercial_name?.trim()) {
        payload.commercial_name = data.commercial_name;
      }

      if (data?.birthdate?.trim()) {
        payload.birthdate = data.birthdate;
      }

      const response = await axios.put(`/admin/v1/applicant/${id}`, payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["external-users"],
      });
      queryClient.invalidateQueries({
        queryKey: ["external-user", id],
      });
    },
  });
};

export const getExternalUser = async (id: string) => {
  const response = await axios.get(`/admin/v1/applicant/${id}`);
  return response.data;
};

export const useGetExternalUser = (id: string) => {
  return useQuery({
    queryKey: ["external-user", id],
    queryFn: async () => {
      const response = await getExternalUser(id);
      return response.data;
    },
    enabled: !!id,
  });
};

export const useActivateExternalUser = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: { description: string }) => {
      const response = await axios.patch(`/admin/v1/applicant/${id}`, {
        ...body,
        status: 1,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["external-users"],
      });
      queryClient.invalidateQueries({
        queryKey: ["external-user", id],
      });
    },
  });
};

export const useInactivateExternalUser = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: { description: string }) => {
      const response = await axios.patch(`/admin/v1/applicant/${id}`, {
        ...body,
        status: 0,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["external-users"],
      });
      queryClient.invalidateQueries({
        queryKey: ["external-user", id],
      });
    },
  });
};

export const useDeleteExternalUser = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const response = await axios.delete(`/admin/v1/applicant/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["external-users"],
      });
    },
  });
};

export const useCheckDuplicateDocument = () => {
  return useMutation({
    mutationFn: async (data: {
      document_type: string;
      document_number: string;
      exclude_id?: string;
    }) => {
      const params = new URLSearchParams();
      params.append("field", "document_number");
      params.append("value", data.document_number);
      if (data.document_type) {
        params.append(
          "aditional_constraints[document_type]",
          data.document_type,
        );
      }

      const response = await axios.get(
        `/admin/v1/applicant/validate-field?${params.toString()}`,
      );
      return response.data;
    },
  });
};

export const useCheckDuplicateEmail = () => {
  return useMutation({
    mutationFn: async (data: { email: string; exclude_id?: string }) => {
      const params = new URLSearchParams();
      params.append("field", "email");
      params.append("value", data.email);

      const response = await axios.get(
        `/admin/v1/applicant/validate-field?${params.toString()}`,
      );
      return response.data;
    },
  });
};

export const useResetExternalUserPassword = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const response = await axios.post(
        `/admin/v1/applicant/${id}/change-password`,
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["external-users"],
      });
      queryClient.invalidateQueries({
        queryKey: ["external-user", id],
      });
    },
  });
};

export const createExternalUserDetailsBreadcrumb = (
  queryClient: import("@tanstack/react-query").QueryClient,
  id: string,
) => {
  return async () => {
    const userId = id;
    try {
      const user = await queryClient.ensureQueryData({
        queryKey: ["external-user", userId],
        queryFn: async () => {
          const response = await getExternalUser(userId);
          return response.data;
        },
        staleTime: 5 * 60 * 1000, // 5 minutes
      });
      return {
        data: {
          ...user,
          name: user.last_name
            ? `${user.first_name} ${user.last_name}`
            : user.first_name,
        },
      };
    } catch (error) {
      console.error("Error fetching external user for breadcrumb:", error);
      return {
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  };
};
